import type { HTTPService } from "@soft-db/http";
import type { AlertSummary, WeeklyLimitSummary } from ".";
import type { Coordinates, Image } from "./shared";
import type { StationPartialStatus } from "./station-api";
import type { VibrationCriteriaSummary } from "./vibration-criteria-api";

export type ProjectDashboard = {
	default: string;
	user: string;
};

export type Project = {
	id: string;
	legacyId: string;
	name: string;
	description: string;
	coordinates: Coordinates;
	timezone: string;
	scenarioPeriods: number[];
	alerts: AlertSummary[];
	image: Image;
	station: string;
	group: string;
	archived: boolean;
	dashboards: ProjectDashboard;
	vibrationCriterias: VibrationCriteriaSummary[];
	weeklyLimits: WeeklyLimitSummary[];
	files: Omit<ProjectFile, "url">[];
	status: StationPartialStatus | null;
	subscribers: string[];
	emailRecipients: string[];
	phoneNumberRecipients: string[];
};

export type ProjectFile = {
	id: string;
	name: string;
	mimeType: string;
	url: string;
};

export type CreateProject = {
	name: string;
	description: string;
	coordinates: Coordinates;
};

export type UpdateProject = {
	name?: string;
	description?: string;
	scenarioPeriods?: number[];
	coordinates?: Coordinates;
	subscribers?: string[];
	emailRecipients?: string[];
	phoneNumberRecipients?: string[];
};

export class ProjectApi {
	constructor(private _http: HTTPService) {}

	async fetch(id: string) {
		return this._http.get<Project>(`/projects/${id}`);
	}

	async fetchFavorites() {
		return this._http.get<Project[]>("/projects/favorites");
	}

	async fetchContext() {
		return this._http.get<Project[]>("/projects");
	}

	async fetchAll() {
		return this._http.get<Project[]>("/projects", { params: { all: true } });
	}

	async create(project: CreateProject) {
		return this._http.post<Project>("/projects", project);
	}

	async update(id: string, difference: Partial<Project>) {
		return this._http.patch<Project>(`/projects/${id}`, difference);
	}

	async archive(id: string) {
		return this._http.delete<Project>(`/projects/${id}`);
	}

	async restore(id: string) {
		return this._http.post<Project>(`/projects/${id}/restore`);
	}

	async subscribe(id: string) {
		return await this._http.put<Project>(`projects/${id}/subscription`);
	}

	async unsubscribe(id: string) {
		return await this._http.delete<Project>(`projects/${id}/subscription`);
	}

	async updateImage(id: string, file: File) {
		return this._http.postFile<Project>(`/projects/${id}/image`, "file", file);
	}

	async deleteImage(id: string) {
		return this._http.delete<Project>(`/projects/${id}/image`);
	}

	async fetchFile(id: string, fileId: string) {
		return this._http.get<ProjectFile>(`/projects/${id}/files/${fileId}`);
	}

	async uploadFile(id: string, file: File) {
		const form = new FormData();
		form.append("file", file);
		form.append("name", file.name);
		return this._http.post<ProjectFile>(`/projects/${id}/files`, form);
	}

	async deleteFile(id: string, fileId: string) {
		return this._http.delete<Project>(`projects/${id}/files/${fileId}`);
	}
}
